import React, { useMemo } from 'react';
import { FormContext, FormStateBasic } from 'informed';
import { useTranslation } from 'react-i18next';

import ClientConfigDetail from '../../stores/models/ClientConfigDetailModel';
import {
  UserAuthHandlerMachineSendType,
  UserAuthHandlerMachineStateType
} from '../../types/UserAuthHandlerMachineTypes';
import { UserAuthInputFieldValidationSchema } from '../../utils/UserAuthInputFieldsValidationSchema';

import { AuthOtpInputCard } from '../AuthOtpInputCard';
import AuthPhoneNumberInputCard from '../AuthPhoneNumberInputCard';

import {
  AuthFormContainer,
  AuthFormSectionContainer,
  AuthFormSectionFooterLogoContainer,
  AuthFormSectionFooterLogoImg,
  AuthFormSectionHeaderLogoContainer,
  AuthFormSectionHeaderLogoImg
} from './styledComponents';

interface AuthFormSectionProps {
  userAuthHandlerMachineSend: UserAuthHandlerMachineSendType;
  userAuthHandlerMachineState: UserAuthHandlerMachineStateType;
  clientConfigDetails: ClientConfigDetail;
}

const AuthFormSection: React.FC<AuthFormSectionProps> = ({
  userAuthHandlerMachineSend,
  userAuthHandlerMachineState,
  clientConfigDetails
}) => {
  const { t } = useTranslation();

  const {
    headerImgUrl,
    footerImgUrl,
    troubleLoggingIframeUrl,
    headerImgAltText
  } = clientConfigDetails;

  const validationSchemaObject = useMemo(
    () => UserAuthInputFieldValidationSchema(t),
    []
  );

  const { values: contextValues, isOtpSentToUser } =
    userAuthHandlerMachineState.context;

  const renderAuthPhoneNumberInputCard = (
    errors: FormStateBasic['errors'],
    touched: FormStateBasic['touched'],
    values: FormStateBasic['values']
  ) => {
    return (
      <AuthPhoneNumberInputCard
        errors={errors}
        touched={touched}
        values={values}
        validationSchema={validationSchemaObject}
        userAuthHandlerMachineSend={userAuthHandlerMachineSend}
        userAuthHandlerMachineState={userAuthHandlerMachineState}
      />
    );
  };

  const renderAuthOtpInputCard = (formState: FormStateBasic) => {
    if (!userAuthHandlerMachineState.context.authMachineRef) return null;
    const { authMachineRef } = userAuthHandlerMachineState.context;

    return (
      <AuthOtpInputCard
        validationSchemaObject={validationSchemaObject}
        authMachineRef={authMachineRef}
        formState={formState}
        troubleLoggingIFrameURL={troubleLoggingIframeUrl}
        userAuthHandlerMachineSend={userAuthHandlerMachineSend}
        userAuthHandlerMachineState={userAuthHandlerMachineState}
      />
    );
  };

  const getInitialValues = (field: string) => {
    if (contextValues[field]) {
      return contextValues[field];
    }
    return '';
  };

  const renderAuthFormContainer = (): React.ReactElement => {
    return (
      <AuthFormContainer
        allowEmptyStrings={true}
        className={'authFormContainer'}
        initialValues={{
          otp: getInitialValues('otp'),
          phoneNumber: getInitialValues('phoneNumber'),
          signUpTermsAndConditionsCheckbox: true
        }}
      >
        {(formProps: FormContext) => {
          const { formState } = formProps;
          const { values, touched, errors } = formState;

          return (
            <>
              {!isOtpSentToUser &&
                renderAuthPhoneNumberInputCard(errors, touched, values)}
              {isOtpSentToUser && renderAuthOtpInputCard(formState)}
            </>
          );
        }}
      </AuthFormContainer>
    );
  };

  const renderAuthFormSectionHeader = () => {
    return (
      headerImgUrl && (
        <AuthFormSectionHeaderLogoContainer
          className={'authFormSectionHeaderLogoContainer'}
        >
          <AuthFormSectionHeaderLogoImg
            src={headerImgUrl}
            alt={headerImgAltText}
            className={'auth-form-section-header-logo-img'}
          />
        </AuthFormSectionHeaderLogoContainer>
      )
    );
  };

  const renderAuthFormSectionFooter = () => {
    return (
      footerImgUrl && (
        <AuthFormSectionFooterLogoContainer
          className={'authFormSectionFooterLogoContainer'}
        >
          <AuthFormSectionFooterLogoImg src={footerImgUrl} />
        </AuthFormSectionFooterLogoContainer>
      )
    );
  };

  return (
    <AuthFormSectionContainer className={'auth-form-section-container'}>
      {renderAuthFormSectionHeader()}
      {renderAuthFormContainer()}
      {renderAuthFormSectionFooter()}
    </AuthFormSectionContainer>
  );
};

export { AuthFormSection };
